import { AnimatePresence } from 'framer-motion';
import { SMSMetrics } from './use-sms-metrics';
import { useSegmentCounter } from './use-segment-counter';
import { SegmentIndicator, AnimatedBadge, AnimatedCount } from './animated-components';
import { Info } from 'lucide-react';
import { TooltipProvider } from '@/components/ui/tooltip';
import { EditorTooltip, EditorTooltipTrigger, EditorTooltipContent } from './editor-tooltip';
import { hasMMSContent } from './mms-utils';
import { Descendant } from 'slate';

interface EditorMetricsProps {
  metrics: SMSMetrics;
  value: Descendant[];
  hasVariables?: boolean;
}

export function EditorMetrics({ metrics, value, hasVariables }: EditorMetricsProps) {
  const { segmentDiff, onAnimationComplete } = useSegmentCounter(metrics.totalSegments);
  const isMMS = hasMMSContent(value);

  return (
    <div className="absolute bottom-2 right-2 flex items-center gap-2 px-3">
      <div className="flex items-center gap-1">
        <AnimatedCount
          value={metrics.usedCharacters}
          total={metrics.segmentSize}
          className="text-xs text-foreground font-mono tabular-nums"
        />
        {hasVariables && (
          <TooltipProvider>
            <EditorTooltip>
              <EditorTooltipTrigger>
                <Info className="h-3 w-3 text-muted-foreground" />
              </EditorTooltipTrigger>
              <EditorTooltipContent>
                <p className="text-xs">
                  Character count is estimated using variable names or default values
                </p>
              </EditorTooltipContent>
            </EditorTooltip>
          </TooltipProvider>
        )}
      </div>

      <AnimatePresence mode="popLayout">
        {isMMS && (
          <AnimatedBadge key="mms-badge" variant="mms">
            1 MMS
          </AnimatedBadge>
        )}
        <div key="sms-container" className="relative">
          {segmentDiff !== 0 && (
            <SegmentIndicator
              key="segment-indicator"
              value={segmentDiff}
              onComplete={onAnimationComplete}
            />
          )}
          <AnimatedBadge key="sms-badge" variant="sms">
            {metrics.totalSegments} SMS
          </AnimatedBadge>
        </div>
      </AnimatePresence>
    </div>
  );
}
